import { Menu, VisuallyHidden } from "@mantine/core";
import {
  ClipboardPaste,
  Copy,
  CopyPlus,
  Eraser,
  Eye,
  EyeOff,
  FlipHorizontal2,
  Lock,
  LockOpen,
  RotateCw,
  Scissors,
  Trash2,
  Undo2,
  Unlink,
  Volume2,
  VolumeX,
} from "lucide-react";
import { Fragment, useId, type ReactNode } from "react";

import { MENU_KEYS } from "./keys";
import { KeyCaps } from "./ShortcutsModal";
import classes from "./editor.module.css";

/** Where a menu opens: the pointer's place (or the block's, from the keyboard), on what clip. */
export interface MenuPlace {
  x: number;
  y: number;
  clipId: string | null;
}

/**
 * One item: its live name and what it does; undefined does nothing, and is shown disabled. An
 * item with `items` opens them beside it (live's Flip).
 */
export interface MenuAction {
  label: keyof typeof MENU_KEYS;
  run: (() => void) | undefined;
  items?: readonly MenuAction[];
}

const ICONS: Record<MenuAction["label"], ReactNode> = {
  Copy: <Copy size={14} />,
  Paste: <ClipboardPaste size={14} />,
  "Paste at Playhead": <ClipboardPaste size={14} />,
  Duplicate: <CopyPlus size={14} />,
  "Split at Playhead": <Scissors size={14} />,
  "Separate Audio": <Unlink size={14} />,
  // Spec remove-background.md.
  "Remove Background": <Eraser size={14} />,
  "Restore Original": <Undo2 size={14} />,
  Mute: <VolumeX size={14} />,
  Unmute: <Volume2 size={14} />,
  Lock: <Lock size={14} />,
  Unlock: <LockOpen size={14} />,
  Hide: <Eye size={14} />,
  Show: <EyeOff size={14} />,
  Flip: <FlipHorizontal2 size={14} />,
  "Flip Horizontal": null,
  "Flip Vertical": null,
  "Rotate 90°": <RotateCw size={14} />,
  Delete: <Trash2 size={14} />,
};

/**
 * Live's context menus, the timeline's (`editor-depth/05`) and the canvas's (`03`, `04`): groups
 * of items apart, Delete in red, one line each. Each item shows its keys as plain grey glyphs;
 * an item that does nothing there is disabled; Flip opens its two beside it. It opens at the pointer, or at a block from the
 * keyboard (Shift+F10, the menu key). `name` names it for a screen reader.
 */
export function TimelineMenu(props: {
  place: MenuPlace | null;
  groups: readonly (readonly MenuAction[])[];
  mac: boolean;
  name?: string;
  onClose: () => void;
}) {
  const { place, groups, mac, onClose } = props;
  // Named by its own words: Mantine would name it by its anchor, which has none.
  const nameId = useId();
  return (
    <Menu
      opened={place !== null}
      onChange={(opened) => {
        if (!opened) onClose();
      }}
      position="bottom-start"
      offset={2}
      // As wide as its longest line: live's items never wrap ("Split at Playhead", issue 350).
      width="max-content"
      shadow="md"
      classNames={{ itemLabel: classes.menuLabel }}
    >
      <Menu.Target>
        {/* The menu's anchor: a point at the place it opens. */}
        <span
          className={classes.menuAnchor}
          style={{ left: place?.x ?? 0, top: place?.y ?? 0 }}
          aria-hidden
        />
      </Menu.Target>
      <Menu.Dropdown aria-labelledby={nameId}>
        <VisuallyHidden id={nameId}>
          {props.name ?? (place?.clipId == null ? "Timeline" : "Clip")}
        </VisuallyHidden>
        {groups.map((group, at) => (
          <Fragment key={group.map((item) => item.label).join()}>
            {at > 0 ? <Menu.Divider /> : null}
            {group.map((item) => {
              const row = (each: MenuAction) => (
                <Menu.Item
                  key={each.label}
                  leftSection={ICONS[each.label]}
                  rightSection={<KeyCaps keys={MENU_KEYS[each.label] ?? []} mac={mac} plain />}
                  disabled={each.run === undefined}
                  {...(each.label === "Delete" ? { color: "red" } : {})}
                  onClick={() => {
                    each.run?.();
                    onClose();
                  }}
                >
                  {each.label}
                </Menu.Item>
              );
              if (item.items === undefined) return row(item);
              const open = item.items.some((each) => each.run !== undefined);
              return (
                <Menu.Sub key={item.label} position="right-start" offset={4}>
                  <Menu.Sub.Target>
                    <Menu.Sub.Item leftSection={ICONS[item.label]} disabled={!open} closeMenuOnClick={false}>
                      {item.label}
                    </Menu.Sub.Item>
                  </Menu.Sub.Target>
                  <Menu.Sub.Dropdown>{item.items.map(row)}</Menu.Sub.Dropdown>
                </Menu.Sub>
              );
            })}
          </Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
